import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { FileSpreadsheet } from 'lucide-react'

interface CompanyConfig {
  companyType: 'ab' | 'enskild' | 'handelsbolag' | 'kommanditbolag' | 'ekonomisk_forening';
  annualTurnover: 'under_1m' | '1m_to_40m' | 'over_40m';
  taxReportingPeriod: 'monthly' | 'quarterly' | 'annually';
  vatReportingPeriod: 'monthly' | 'quarterly' | 'annually';
  toleranceDays: number;
}

interface CompanyConfigPanelProps {
  config: CompanyConfig;
  onConfigChange: (config: CompanyConfig) => void;
  onContinue: () => void;
}

export function CompanyConfigPanel({ config, onConfigChange, onContinue }: CompanyConfigPanelProps) {
  const handleFieldChange = (field: keyof CompanyConfig, value: string | number) => {
    onConfigChange({
      ...config,
      [field]: value
    });
  };

  const getCompanyTypeDescription = (type: string) => {
    switch (type) {
      case 'ab': return 'Månadsvis redovisning för omsättning >40 mkr, annars kvartalsvis';
      case 'enskild': return 'Månadsvis eller kvartalsvis beroende på omsättning';
      case 'handelsbolag': return 'Oftast kvartalsvis redovisning';
      case 'kommanditbolag': return 'Oftast kvartalsvis redovisning';
      case 'ekonomisk_forening': return 'Varierar baserat på omsättning';
      default: return '';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5" />
          Företagskonfiguration
        </CardTitle>
        <CardDescription>
          Ange företagsinformation för mer exakta förfallodatum och analyser enligt gällande skattelagstiftning.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Företagstyp */}
          <div className="space-y-2">
            <label className="text-sm font-medium block">Företagstyp</label>
            <select 
              value={config.companyType}
              onChange={(e) => handleFieldChange('companyType', e.target.value)}
              className="w-full p-3 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="ab">Aktiebolag (AB)</option>
              <option value="enskild">Enskild näringsidkare</option>
              <option value="handelsbolag">Handelsbolag (HB)</option>
              <option value="kommanditbolag">Kommanditbolag (KB)</option>
              <option value="ekonomisk_forening">Ekonomisk förening</option>
            </select>
            <p className="text-xs text-muted-foreground">
              {getCompanyTypeDescription(config.companyType)}
            </p>
          </div>

          {/* Omsättning */}
          <div className="space-y-2">
            <label className="text-sm font-medium block">Årsomsättning</label>
            <select 
              value={config.annualTurnover}
              onChange={(e) => handleFieldChange('annualTurnover', e.target.value)}
              className="w-full p-3 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="under_1m">Under 1 miljon kr</option>
              <option value="1m_to_40m">1-40 miljoner kr</option>
              <option value="over_40m">Över 40 miljoner kr</option>
            </select>
            <p className="text-xs text-muted-foreground">
              {config.annualTurnover === 'over_40m' ? 'Kräver månadsvis redovisning' : 'Kan välja månadsvis eller kvartalsvis'}
            </p>
          </div>

          {/* Skatteredovisningsperiod */}
          <div className="space-y-2">
            <label className="text-sm font-medium block">Skatteredovisningsperiod</label>
            <select 
              value={config.taxReportingPeriod}
              onChange={(e) => handleFieldChange('taxReportingPeriod', e.target.value)}
              className="w-full p-3 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={config.annualTurnover === 'over_40m'}
            >
              <option value="monthly">Månadsvis</option>
              <option value="quarterly">Kvartalsvis</option>
              <option value="annually">Årligen</option>
            </select>
            <p className="text-xs text-muted-foreground">
              {config.annualTurnover === 'over_40m' ? 'Månadsvis krävs för företag >40 mkr' : 'Välj enligt ditt val hos Skatteverket'}
            </p>
          </div>

          {/* Momsredovisningsperiod */}
          <div className="space-y-2">
            <label className="text-sm font-medium block">Momsredovisningsperiod</label>
            <select 
              value={config.vatReportingPeriod}
              onChange={(e) => handleFieldChange('vatReportingPeriod', e.target.value)}
              className="w-full p-3 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="monthly">Månadsvis</option>
              <option value="quarterly">Kvartalsvis</option>
              <option value="annually">Årligen</option>
            </select>
            <p className="text-xs text-muted-foreground">
              Kan skilja sig från skatteredovisningsperiod
            </p>
          </div>

          {/* Tolerans för sena betalningar */}
          <div className="space-y-2 md:col-span-2">
            <label className="text-sm font-medium block">Tolerans för sena betalningar</label>
            <div className="flex items-center space-x-4">
              <input 
                type="range"
                min="0"
                max="7"
                value={config.toleranceDays}
                onChange={(e) => handleFieldChange('toleranceDays', parseInt(e.target.value))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex items-center space-x-2 min-w-[120px]">
                <span className="text-2xl font-bold text-primary">{config.toleranceDays}</span>
                <span className="text-sm text-muted-foreground">
                  {config.toleranceDays === 1 ? 'dag' : 'dagar'}
                </span>
              </div>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Strikt (0 dagar)</span>
              <span>Standard (3 dagar)</span>
              <span>Flexibel (7 dagar)</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {config.toleranceDays === 0 
                ? 'Ingen tolerans - alla sena betalningar flaggas omedelbart'
                : `Betalningar flaggas som sena först ${config.toleranceDays} ${config.toleranceDays === 1 ? 'dag' : 'dagar'} efter förfallodatum. AI:n identifierar alla avvikelser, men de inom toleransen filtreras bort automatiskt.`
              }
            </p>
          </div>
        </div>

        <div className="pt-4 border-t space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Förväntade förfallodatum 2025:</h4>
            <div className="text-sm text-blue-800 space-y-1">
              {config.annualTurnover === 'over_40m' ? (
                // Stora bolag (>40M kr)
                <>
                  <p>• <strong>Preliminärskatt:</strong> 12:e (17:e jan/aug) i månaden efter perioden</p>
                  <p>• <strong>Arbetsgivaravgifter:</strong> 12:e (17:e jan/aug) i månaden efter perioden</p>
                  <p>• <strong>Moms:</strong> 26:e varje månad (alltid månadsvis)</p>
                </>
              ) : (
                // Små bolag (<40M kr)
                <>
                  <p>• <strong>Preliminärskatt:</strong> 12:e (17:e jan/aug) i månaden efter perioden</p>
                  <p>• <strong>Arbetsgivaravgifter:</strong> 12:e (17:e jan/aug) i månaden efter perioden</p>
                  <p>• <strong>Moms:</strong> {config.vatReportingPeriod === 'monthly' 
                    ? '12:e (17:e jan) i månaden efter perioden' 
                    : '12:e endast feb/maj/aug/nov (kvartalsvis)'}</p>
                </>
              )}
              <p className="text-xs pt-2 border-t border-blue-300">* {config.toleranceDays} {config.toleranceDays === 1 ? 'dags' : 'dagars'} tolerans efter förfallodatum</p>
              <p className="text-xs text-blue-600">* Baserat på officiell skattekalender 2025</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Konfiguration sparas för denna session
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button onClick={onContinue} className="px-6">
                    Fortsätt till filuppladdning
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Spara konfigurationen och gå vidare till att ladda upp skattekontodata</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}